import minMax from 'dayjs/plugin/minMax';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

const dayjs = require('dayjs');

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault();

dayjs.extend(minMax);
dayjs.extend(relativeTime);
export { dayjs };

export const DATE_FORMAT = {
  DATE_WITH_TIME: 'MM/DD/YYYY hh:mm:ss A',
  DATE_ONLY: 'MM/DD/YYYY',
  DATE_ONLY_HIPHEN: 'MM-DD-YYYY',
  DAY_WITH_TIME: 'ddd hh:mm A',
  DAY_WITH_HR: 'ddd hh A',
  DAY_ONLY: 'ddd',
  TIME_HR_ONLY: 'h A',
  DATE_YYYY_MM_DD: 'YYYY-MM-DD',
};
export const WEEK_DAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];
export const DAY_DROP_DOWN_OPTIONS = WEEK_DAYS.flatMap((d, index) => {
  const opt = {
    id: `${index}`,
    name: d,
  };
  if (index === 0) return [{ id: '', name: 'All' }, opt];
  return opt;
});

export const toDisplayDate = (
  d: string | number,
  format: string = DATE_FORMAT.DAY_WITH_TIME
) => {
  return dayjs(d).format(format);
};
export const getHour = (d: string) => {
  return dayjs(d).hour();
};
export const getDateDifference = (
  d1: string,
  d2: string,
  unit: string = 'day'
): number => {
  return dayjs(d1).diff(dayjs(d2), unit);
};
export const isValidDate = (d: any) => {
  return d && dayjs(d).isValid();
};
export const getDateIsoString = (d: string) =>
  d && dayjs(d).isValid() ? dayjs(d)?.$d?.toISOString() : null;
export const dateDiffCheck = (
  d1: string,
  d2: string = dayjs(),
  unitOfTime: string = 'days',
  precise: boolean = true
) => {
  return dayjs(d1).diff(dayjs(d2), unitOfTime, precise);
};
export const dateOrdering = ([d1, d2]: string[]) => {
  const dateDifference = dateDiffCheck(d1, d2);
  if (!isNaN(dateDifference) && dateDifference > 0) {
    return [d2, d1];
  } else {
    return [d1, d2];
  }
};
export function getSpecificWeekDate(
  date: string,
  targetDay: number,
  targetHrs?: number
) {
  const dt = new Date(date);
  dt.setDate(dt.getDate() + targetDay - dt.getDay());
  if (typeof targetHrs === 'number') {
    dt.setHours(targetHrs);
  }
  return dt.toISOString();
}

export function getUTCDateTime(dt: string) {
  let date = new Date(dt);

  return new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds()
    )
  ).toISOString();
}

function getTimeAndDateIn12HourClock(timestamp: number): string {
  const date = new Date(timestamp);
  const day = ('0' + date.getDate()).slice(-2);
  const month = ('0' + (date.getMonth() + 1)).slice(-2);
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = ('0' + date.getMinutes()).slice(-2);
  const seconds = ('0' + date.getSeconds()).slice(-2);
  const meridiem = hours >= 12 ? 'PM' : 'AM';

  hours = hours % 12 || 12; // Convert to 12-hour format
  const formattedDate = `${month}/${day}/${year}`;
  const formattedTime = `${hours}:${minutes}:${seconds} ${meridiem}`;

  return `${formattedDate} ${formattedTime}`;
}

function convertTo12HourFormat(
  timeString: string,
  isShortTime: boolean
): string {
  let timeArray: string[] = timeString.split(':');
  let hours: number = parseInt(timeArray[0], 10);
  let minutes: string = timeArray[1];
  let seconds: string = timeArray[2];
  let period: string = hours >= 12 ? 'PM' : 'AM';

  // Convert hours to 12-hour format
  hours = hours % 12 || 12;
  return isShortTime
    ? hours + ':' + minutes + ' ' + period
    : hours + ':' + minutes + ':' + seconds + ' ' + period;
}

function convertUnixTimestampToFormattedDateTime(
  unixTimestamp: number,
  isShortTime: boolean = false
): { formattedDateTime: string; formattedDate: string; timeComponent: string } {
  let inputDateTime: string = new Date(unixTimestamp).toISOString();

  // Extracting date and time components separately
  let dateComponent: string = inputDateTime.split('T')[0];
  let timeComponent: string = convertTo12HourFormat(
    inputDateTime.split('T')[1].slice(0, 8),
    isShortTime
  );

  // Formatting date component to mm/dd/yyyy format
  let dateArray: string[] = dateComponent.split('-');
  let formattedDate: string =
    dateArray[1] + '/' + dateArray[2] + '/' + dateArray[0];

  // Joining date and time components
  let formattedDateTime: string = formattedDate + ' ' + timeComponent;
  return { formattedDateTime, formattedDate, timeComponent };
}

function getDayFromTimestamp(date: string) {
  const dayNames: string[] = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  const dayOfWeek: number = new Date(date).getDay();
  const dayName: string = dayNames[dayOfWeek];
  return dayName;
}

export {
  getTimeAndDateIn12HourClock,
  convertUnixTimestampToFormattedDateTime,
  getDayFromTimestamp,
};
